import { createContext, useContext } from 'react';
import mergeWith from 'lodash.mergewith';
import { isAssumedComponentOrElementObject } from '../util';
import { useHawkinsContext } from './useHawkinsContext';
export var HawkinsPropsOverrideContext = createContext({});
HawkinsPropsOverrideContext.displayName = 'HawkinsPropsOverrideContext';
export function propsMergeFn(val1, val2) {
    // Merging props set to a React component can lead to an infinite loop when the component is rendered.
    // Not sure why. To be safe, never merge component or element props.
    //
    // TODO(investigation): Understand what's going on.
    if (isAssumedComponentOrElementObject(val1) || isAssumedComponentOrElementObject(val2)) {
        // Check only for `undefined` to allow for overriding the prop value with `null`.
        if (val2 !== undefined)
            return val2;
        return val1;
    }
    // Returning `undefined` causes the default merge behavior to be used.
    return undefined;
}
/**
 * A hook that allows for context-aware overriding of a component’s props.
 *
 * @private
 */
export function useProps(componentName, userProps) {
    var _a = useHawkinsContext(), _b = componentName, hawkinsContextProps = _a.defaultProps[_b], loading = _a.loading, compact = _a.compact;
    var _c = useContext(HawkinsPropsOverrideContext), _d = componentName, overrideContextProps = _c[_d];
    // No point in memoizing, since `userProps` will always be a different object.
    //
    // In most cases, there won't be any prop overrides, so return the user props to save time.
    if (!hawkinsContextProps && !overrideContextProps && loading === undefined && compact === undefined)
        return userProps;
    // The order of overrides is:
    //  - Hawkins-provided `HawkinsPropsOverrideContext` props
    //  - User-provided `HawkinsProvider` props
    //  - User-provided props
    return mergeWith(
    // Lodash `merge()` mutates its first argument, so start with a fresh object.
    {}, { loading: loading, compact: compact }, overrideContextProps, hawkinsContextProps, userProps, propsMergeFn);
}
